import type { ReactNode } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { RotateCwIcon } from 'lucide-react';

import { DetailItem } from '@components/display/detail-item';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import { Skeleton } from '@components/ui/skeleton';
import { queryKeys } from '@constants/queryKeys.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import { formatFullAddress } from '@formatters/fullAddress.formatter';
import { cn } from '@lib/utils';
import { companyService } from '@services/companies/service';

export function ProviderCompany() {
  const { id: providerId } = useParams({
    from: '/_layout/(private)/providers/$id/',
  });

  const { data, isError, isPending, isRefetching, refetch } = useQuery({
    queryKey: queryKeys.companies.detail(providerId),
    queryFn: () => companyService.getByOwnerId(providerId),
    staleTime: EStaleTime.OneHour,
  });

  if (isPending) {
    return <CompanyLoading />;
  }

  if (!data?.company && isError) {
    return (
      <Container className="flex flex-col items-center">
        <p className="my-4 w-fit text-center text-sm text-gray-500 2xl:max-w-1/2">
          We couldn't load the company data, something failed during the
          process, please try again
        </p>
        <Button
          className="text-xs"
          isLoading={isRefetching}
          onClick={() => refetch()}
          rounded="md"
          size="sm"
          variant="secondary"
        >
          Retry
          <RotateCwIcon />
        </Button>
      </Container>
    );
  }

  const companyAddress = formatFullAddress({
    city: data.company.address.city,
    number: data.company.address.number,
    postcode: data.company.address.postcode,
    street: data.company.address.street,
  });

  return (
    <Container>
      <div className="grid grid-cols-2 gap-3">
        <DetailItem
          isFullLine
          label="Address"
          value={companyAddress || 'Deleted in Odoo ☠️ ⚠️'}
        />
        <DetailItem
          label="VAT"
          value={data.company.vat || 'Deleted in Odoo ☠️ ⚠️'}
        />
      </div>
    </Container>
  );
}

export function CompanyLoading() {
  return (
    <Container>
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="col-span-2 h-3.5 w-6/12" />
        <Skeleton className="col-span-2 mb-2 h-4 w-full" />
        <Skeleton className="h-3.5 w-6/12" />
        <Skeleton className="h-3.5 w-6/12" />
        <Skeleton className="mb-2 h-4 w-full" />
        <Skeleton className="mb-2 h-4 w-full" />
      </div>
    </Container>
  );
}

function Container({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('relative mb-2', className)}>
      {children}

      <Separator
        className="invisible absolute right-0 bottom-0 h-2/3 lg:visible"
        orientation="vertical"
        variant="dotted"
      />
    </div>
  );
}
